import { z } from "zod";
// Impostazioni → «Collegamenti» (M7 parte 2, IMP-1): in un posto solo lo stato di ogni collegamento dell'azienda, in sola lettura.
// `GET /api/settings/connections` lo legge dai dati che Gioia365 ha già (database di piattaforma e dell'azienda, configurazione del
// server, memoria del giro dei moduli di Meta): nessuna chiamata ai fornitori, nessun segreto, nessun numero di telefono né indirizzo
// email, soltanto stati, conteggi, date e i nomi delle persone del team. La leggono il Super admin e l'Admin dell'azienda (ruolo di
// livello 3 o più) e il Super admin di Gioia365; in «Guarda come…» anche la persona osservata deve avere quel livello (RUOLI-19).
//
// Ogni collegamento ha:
// - `state`: `working` funziona, `incomplete` da completare, `off` spento, `error` qualcosa non va;
// - `lastWorkedAt`: l'ultima volta che ha funzionato (ISO), quando il dato esiste;
// - `missing`: che cosa manca o non va, come codici che l'interfaccia dice in parole comuni (`CONNECTION_MISSING`).
// L'importazione da Close la vede per intero soltanto il Super admin di Gioia365 (INGR-12): per gli altri `detailed: false`, soltanto
// accesa o spenta la sera, senza date, esito né mancanze.
export const CONNECTION_STATES = ["working", "incomplete", "off", "error"];
export const connectionStateSchema = z.enum(CONNECTION_STATES);
/** Che cosa manca, collegamento per collegamento. */
export const CONNECTION_MISSING = [
    // Telefonia
    "telephony_off",
    "telephony_setting_up",
    "telephony_failed",
    "telephony_account_blocked",
    "telephony_check_failed",
    "recording_off",
    // Parco dei cellulari
    "numbers_none",
    "numbers_pending",
    "numbers_failed",
    "people_without_number",
    // WhatsApp ufficiale
    "whatsapp_meta_unavailable",
    "whatsapp_meta_company_off",
    "whatsapp_meta_not_connected",
    "whatsapp_meta_failed",
    "whatsapp_meta_link_error",
    "whatsapp_meta_messages_failed",
    "whatsapp_meta_no_senders",
    "whatsapp_meta_senders_not_ready",
    "whatsapp_meta_templates",
    // WhatsApp Agile
    "whatsapp_agile_unavailable",
    "whatsapp_agile_company_off",
    "whatsapp_agile_no_senders",
    "whatsapp_agile_senders_not_ready",
    "whatsapp_agile_down",
    // Ingressi dei Lead
    "intake_meta_unavailable",
    "intake_meta_no_page",
    "intake_meta_round_failed",
    "intake_meta_no_round",
    "intake_meta_round_stale",
    "intake_door_off",
    "intake_failed_arrivals",
    "intake_no_arrivals",
    // Google
    "google_unavailable",
    "google_nobody",
    "google_failed",
    // Importazione da Close
    "close_no_key",
    "close_dry_run_needed",
    "close_nightly_off",
    "close_last_failed",
    // Email
    "email_off",
];
export const connectionMissingSchema = z.enum(CONNECTION_MISSING);
/** Una persona del team, con il nome di adesso: mai il numero né l'email. */
export const connectionPersonSchema = z.object({ membershipId: z.string(), displayName: z.string() });
const base = {
    state: connectionStateSchema,
    lastWorkedAt: z.string().nullable(),
    missing: z.array(connectionMissingSchema),
};
/** La telefonia dell'azienda (TEL-1, TEL-4, TEL-7): accesa, pronta, e se le chiamate si registrano (`null` se non è accesa). */
export const telephonyConnectionStatusSchema = z.object({ ...base, recording: z.boolean().nullable() });
/**
 * Il parco dei cellulari dell'ufficio (TEL-3): quanti, quanti verificati, in verifica e non riusciti, quanti verificati sono dati ad
 * almeno una persona, e le persone attive che possono chiamare senza un cellulare.
 */
export const callerNumbersConnectionStatusSchema = z.object({
    ...base,
    total: z.number().int(),
    verified: z.number().int(),
    pending: z.number().int(),
    failed: z.number().int(),
    assigned: z.number().int(),
    peopleWithoutNumber: z.array(connectionPersonSchema),
});
/** Un WhatsApp automatico (l'ufficiale o Agile, WA-1, WA-2): quante persone lo hanno pronto e quante no. */
export const automaticWhatsAppConnectionStatusSchema = z.object({ ...base, ready: z.number().int(), notReady: z.number().int() });
/** Il WhatsApp ufficiale, con quanti dei modelli dell'azienda Meta ha approvato (WA-14). */
export const officialWhatsAppConnectionStatusSchema = automaticWhatsAppConnectionStatusSchema.extend({ approvedTemplates: z.number().int(), templates: z.number().int() });
/** Il WhatsApp a mano, sempre acceso (WA-2): quante persone lo usano. */
export const manualWhatsAppConnectionStatusSchema = z.object({ ...base, people: z.number().int() });
/** Il sito e le landing, GoHighLevel (INGR-1): quanti arrivi degli ultimi 7 giorni non sono entrati. */
export const doorIntakeConnectionStatusSchema = z.object({ ...base, failedRecently: z.number().int() });
/** I moduli di Meta (INGR-21), come le porte, con la pagina Facebook collegata, come la mostra «Ingressi dei Lead». */
export const metaIntakeConnectionStatusSchema = doorIntakeConnectionStatusSchema.extend({ pageName: z.string().nullable() });
/** Google Calendar (R7): chi lo ha collegato e chi lo ha collegato ma non funziona più, fra le persone attive. */
export const googleConnectionStatusSchema = z.object({ ...base, connectedPeople: z.array(connectionPersonSchema), failedPeople: z.array(connectionPersonSchema) });
/**
 * L'importazione da Close della sera (INGR-12): `detailed` soltanto per il Super admin di Gioia365, che vede anche la prossima e com'è
 * andata l'ultima; per gli altri soltanto lo stato (`working` accesa, `off` spenta), senza date né mancanze.
 */
export const CLOSE_NIGHTLY_LAST_OUTCOMES = ["running", "completed", "interrupted", "failed"];
export const closeImportConnectionStatusSchema = z.object({
    ...base,
    detailed: z.boolean(),
    nextAt: z.string().nullable(),
    lastOutcome: z.enum(CLOSE_NIGHTLY_LAST_OUTCOMES).nullable(),
});
/** Le email di sistema (inviti, reimpostazione della password): partono davvero o si consegnano a mano. */
export const emailConnectionStatusSchema = z.object(base);
export const connectionsResponseSchema = z.object({
    telephony: telephonyConnectionStatusSchema,
    callerNumbers: callerNumbersConnectionStatusSchema,
    whatsappOfficial: officialWhatsAppConnectionStatusSchema,
    whatsappAgile: automaticWhatsAppConnectionStatusSchema,
    whatsappManual: manualWhatsAppConnectionStatusSchema,
    /** Le persone attive che mandano WhatsApp senza un WhatsApp impostato. */
    peopleWithoutWhatsApp: z.array(connectionPersonSchema),
    intakeMeta: metaIntakeConnectionStatusSchema,
    intakeWeb: doorIntakeConnectionStatusSchema,
    intakeGhl: doorIntakeConnectionStatusSchema,
    google: googleConnectionStatusSchema,
    closeImport: closeImportConnectionStatusSchema,
    email: emailConnectionStatusSchema,
});
//# sourceMappingURL=connections.js.map